import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands'
import { javascript } from '@codemirror/lang-javascript'
import { bracketMatching, indentOnInput } from '@codemirror/language'
import { linter, lintGutter, type Diagnostic } from '@codemirror/lint'
import { EditorState } from '@codemirror/state'
import {
  EditorView,
  drawSelection,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
} from '@codemirror/view'
import type { Recorder } from '@coderaw/log'
import { countTokens } from '@coderaw/runtime'
import { noPasteExtension, recordingExtension } from './recording'
import { plotSyntax, plotTheme } from './theme'

export type RecordingEditorOptions = {
  readonly parent: HTMLElement
  readonly recorder: Recorder
  readonly onChange: (doc: string) => void
  readonly onBlockedPaste: () => void
  readonly doc?: string
  readonly assisted?: () => boolean
}

export type RecordingEditor = {
  readonly view: EditorView
  readonly doc: () => string
  readonly focus: () => void
  readonly destroy: () => void
}

/** Turns a countTokens error into a CodeMirror diagnostic on the right line. */
export const tokenDiagnostics = (view: EditorView): readonly Diagnostic[] => {
  const doc = view.state.doc
  const result = countTokens(doc.toString())
  if (!result.error) return []
  const lineNumber = Math.min(Math.max(1, result.error.line), doc.lines)
  const line = doc.line(lineNumber)
  const from = Math.min(line.from + result.error.column, line.to)
  return [
    { from, to: Math.min(from + 1, line.to), severity: 'error', message: result.error.message },
  ]
}

/**
 * CodeMirror with history, highlighting and lint, and with nothing that
 * inserts text you did not type: no autocomplete, no snippets, no bracket
 * closing. Paste is blocked. Every change lands in the recorder.
 */
export const createRecordingEditor = (options: RecordingEditorOptions): RecordingEditor => {
  const state = EditorState.create({
    doc: options.doc ?? '',
    extensions: [
      lineNumbers(),
      highlightActiveLineGutter(),
      highlightActiveLine(),
      drawSelection(),
      history(),
      bracketMatching(),
      indentOnInput(),
      javascript(),
      plotSyntax,
      plotTheme,
      lintGutter(),
      linter((view) => [...tokenDiagnostics(view)], { delay: 300 }),
      keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
      recordingExtension(options.recorder, options.assisted),
      noPasteExtension(options.onBlockedPaste),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) options.onChange(update.state.doc.toString())
      }),
      EditorView.contentAttributes.of({ 'aria-label': 'plot code', spellcheck: 'false' }),
    ],
  })
  const view = new EditorView({ state, parent: options.parent })
  return {
    view,
    doc: () => view.state.doc.toString(),
    focus: () => view.focus(),
    destroy: () => view.destroy(),
  }
}
